/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Tooltip, useTheme } from '@mui/material';
import React from 'react';
import { ProductStatus } from '@opengeoweb/api';
import { getBlockStyle } from './warningBlockColors';
import { WarningBlockMenu } from './WarningBlockMenu';
import { getBlockSize, getEffectiveStatus } from './warningsTimelineUtils';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import type { Warning } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import { isCancelAirmet, isCancelSigmet } from '../../utils/sig-airmet-util';
import { isExpireMessage } from '../../utils/warningUtils';
import type { WarningListItemActions } from '../PublicWarningList/WarningListItemMenu';
import type { WarningLevel } from '../PublicWarningsForm/LevelField';
import {
  allPhenomenaDict,
  getPhenomenaIcon,
} from '../PublicWarningsForm/PhenomenonField';
import WarningsAvatar from '../PublicWarningsFormDialog/WarningsAvatar';
import { probablityOptions } from '../WarningEvents/WarningEventForm/WarningEventItem';

interface WarningBlockProps {
  warning: Warning;
  highlighted?: boolean;
  selected?: boolean;
  left: number;
  onHoverWarning?: (warningId?: string) => void;
  onSelectWarning?: (warning: Warning) => void;
  warningListItemActions?: WarningListItemActions;
  top: number;
  width: number;
}

const MIN_WIDTH_FOR_LETTER = 75;

/**
 * Gets the author/editor from a warning.
 * For airmet/sigmet warnings, author is in sigmetAirmetDetails.
 * For public/maritime warnings, editor is on the base warning.
 */
const getWarningAuthor = (warning: Warning): string | undefined => {
  if ('sigmetAirmetDetails' in warning) {
    return warning.sigmetAirmetDetails?.author ?? warning.editor;
  }
  return warning.editor;
};

const formatPeriod = (from: string, until: string): string => {
  const ff = new Date(from);
  const uu = new Date(until);
  const dateOpts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  };
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  };
  const fDate = ff.toLocaleDateString('en-GB', dateOpts);
  const uDate = uu.toLocaleDateString('en-GB', dateOpts);
  const fTime = ff.toLocaleTimeString('en-GB', timeOpts);
  const uTime = uu.toLocaleTimeString('en-GB', timeOpts);
  return fDate === uDate
    ? `${fDate} ${fTime} – ${uTime} UTC`
    : `${fDate} ${fTime} – ${uDate} ${uTime} UTC`;
};

const getTooltipContent = (
  warning: Warning,
  t: (key: string) => string,
): React.ReactNode => {
  const { warningDetail, status, type } = warning;
  const author = getWarningAuthor(warning);
  const isAviation = type === 'sigmet' || type === 'airmet';
  let warningTypeLabel: string | null = null;
  if (type === 'maritimeWarningCoastal' || type === 'maritimeWarningNorthSea') {
    warningTypeLabel = t('timeline-tooltip-maritime-warning');
  } else if (!isAviation) {
    warningTypeLabel = t('timeline-tooltip-public-warning');
  }

  const phenomenonEntry = allPhenomenaDict[warningDetail.phenomenon];

  const phenomenonLabel = phenomenonEntry
    ? t(phenomenonEntry.translationKey)
    : warningDetail.phenomenon;

  const probability =
    type === 'warningsEvent'
      ? probablityOptions.find(
          (probability) => probability.value === warningDetail.probability,
        )?.name
      : warningDetail.probability;

  const line1 = isAviation
    ? [type.toUpperCase(), phenomenonLabel].filter(Boolean).join(' · ')
    : [
        phenomenonLabel,
        isExpireMessage(warning)
          ? t('warning-level-minor')
          : warningDetail.level,
        warningDetail.probability ? `${probability}%` : null,
      ]
        .filter(Boolean)
        .join(' · ');

  const period =
    warningDetail.validFrom && warningDetail.validUntil
      ? formatPeriod(warningDetail.validFrom, warningDetail.validUntil)
      : null;

  const meta = [author, status].filter(Boolean).join(' · ');

  return (
    <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
      {warningTypeLabel ? (
        <Box sx={{ fontWeight: 600 }}>{warningTypeLabel}</Box>
      ) : null}
      <Box sx={{ fontWeight: warningTypeLabel ? 400 : 600 }}>{line1}</Box>
      {period ? <Box>{period}</Box> : null}
      {meta ? <Box>{meta}</Box> : null}
    </Box>
  );
};

export const WarningBlock = React.memo(
  ({
    warning,
    highlighted = false,
    selected = false,
    left,
    onHoverWarning,
    onSelectWarning,
    top,
    warningListItemActions,
    width,
  }: WarningBlockProps) => {
    const isHighlighted = highlighted || selected;
    const { t } = useWarningsTranslation();
    const theme = useTheme();
    const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
    const [isTooltipSuppressed, setIsTooltipSuppressed] = React.useState(false);
    const isTooltipSuppressedRef = React.useRef(false);
    const { warningDetail, type } = warning;
    const author = getWarningAuthor(warning);
    const isAviation = type === 'sigmet' || type === 'airmet';
    const isExpiryMessage = isExpireMessage(warning);
    const level = isExpiryMessage
      ? null
      : (warningDetail.level as WarningLevel) || null;
    const letter = level ? t(`warning-level-letter-${level}`) : '';
    const effectiveStatus = getEffectiveStatus(warning);

    const { background, border, iconColor, letterColor } = getBlockStyle(
      level,
      effectiveStatus,
      isAviation,
      isExpiryMessage,
    );

    const isCancelledAviation =
      isAviation &&
      (warning.productStatus === ProductStatus.CANCELLED ||
        ((isCancelAirmet(warning.sigmetAirmetDetails) ||
          isCancelSigmet(warning.sigmetAirmetDetails)) &&
          effectiveStatus !== PublicWarningStatus.DRAFT));
    const isExpired = effectiveStatus === PublicWarningStatus.EXPIRED;
    const cancellationColors =
      theme.palette.geowebColors.cancelledWarningTimeline;
    const cancellationStyle = isExpired
      ? cancellationColors.expired
      : cancellationColors.published;
    const cancellationHoverStyle = isExpired
      ? cancellationColors.expiredHover
      : cancellationColors.publishedHover;
    const size = getBlockSize(width);
    const highlightOutlineColor =
      theme.palette.geowebColors.typographyAndIcons.iconLinkActive;

    const elements: React.ReactNode[] = [];

    if (
      !isCancelledAviation &&
      !!letter &&
      (size === 'l' || width >= MIN_WIDTH_FOR_LETTER)
    ) {
      elements.push(
        <Box
          key="letter"
          sx={{
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '80%',
            color: letterColor,
          }}
        >
          {letter}
        </Box>,
      );
    }

    if (!isCancelledAviation && (size === 'l' || size === 's')) {
      elements.push(
        <Box
          key="icon"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            fontSize: 18,
          }}
        >
          {getPhenomenaIcon(warningDetail.phenomenon) || null}
        </Box>,
      );
    }

    if (!isCancelledAviation && size === 'l') {
      elements.push(
        <Box
          key="avatar"
          sx={{ border: '0.5px solid white', borderRadius: '50%' }}
        >
          <WarningsAvatar editor={author ?? ''} />
        </Box>,
      );
    }

    if (isCancelledAviation) {
      if (size === 'l' || size === 's') {
        elements.push(
          <Box
            key="sequence"
            sx={{ fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {warning.sigmetAirmetDetails.sequence}
          </Box>,
        );
        if (size === 'l') {
          elements.push(
            <Box
              key="cancellation-status"
              sx={{ fontSize: 16, whiteSpace: 'nowrap' }}
            >
              {isExpired ? 'CNLD' : 'CNL'}
            </Box>,
          );
        }
      } else if (size === 'xs') {
        elements.push(
          <Box
            data-testid="cancelled-warning-icon"
            key="cancelled-icon"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            {getPhenomenaIcon(warningDetail.phenomenon) || null}
          </Box>,
        );
      }
    }

    const warningMenu =
      !isCancelledAviation && size !== 'xxs' ? (
        <WarningBlockMenu
          effectiveStatus={effectiveStatus}
          hasWarningContent={elements.length > 0}
          isAviation={isAviation}
          level={level}
          onMenuInteraction={(): void => {
            isTooltipSuppressedRef.current = true;
            setIsTooltipOpen(false);
            setIsTooltipSuppressed(true);
          }}
          onMenuLeave={(): void => {
            isTooltipSuppressedRef.current = false;
            setIsTooltipSuppressed(false);
          }}
          warning={warning}
          warningListItemActions={warningListItemActions}
        />
      ) : null;
    let warningBlockPadding = '4px 8px';
    if (warningMenu) {
      warningBlockPadding = size === 'xs' ? '4px 0' : '4px 2px 4px 8px';
    }

    return (
      <Tooltip
        onClose={(): void => setIsTooltipOpen(false)}
        onOpen={(): void => {
          if (isTooltipSuppressed || isTooltipSuppressedRef.current) {
            return;
          }

          setIsTooltipOpen(true);
        }}
        open={isTooltipOpen}
        placement="top"
        title={getTooltipContent(warning, t)}
      >
        <Box
          aria-label={`warning timeline block for ${warning.id ?? ''}`}
          data-highlighted={isHighlighted ? 'true' : 'false'}
          data-testid="warning-block"
          onClick={() => {
            isTooltipSuppressedRef.current = true;
            setIsTooltipOpen(false);
            setIsTooltipSuppressed(true);
            onSelectWarning?.(warning);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              isTooltipSuppressedRef.current = true;
              setIsTooltipOpen(false);
              setIsTooltipSuppressed(true);
              onSelectWarning?.(warning);
            }
          }}
          onMouseEnter={() => onHoverWarning?.(warning.id)}
          onMouseLeave={() => {
            isTooltipSuppressedRef.current = false;
            setIsTooltipSuppressed(false);
            onHoverWarning?.(undefined);
          }}
          role="button"
          sx={{
            position: 'absolute',
            left,
            top,
            width,
            height: 28,
            padding: warningBlockPadding,
            background,
            border,
            ...(isCancelledAviation ? cancellationStyle : {}),
            ...(isCancelledAviation && highlighted
              ? cancellationHoverStyle
              : {}),
            borderRadius: 1,
            boxShadow: isHighlighted
              ? `0 0 0 2px ${highlightOutlineColor}`
              : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: elements.length === 1 ? 'center' : 'space-between',
            overflow: 'hidden',
            zIndex: isHighlighted ? 11 : 1,
            cursor: 'pointer',
            '&:hover': {
              ...(isCancelledAviation ? cancellationHoverStyle : {}),
              boxShadow: `0 0 0 2px ${highlightOutlineColor}`,
              zIndex: 11,
            },
          }}
          tabIndex={0}
        >
          {elements.length ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '1 1 0',
                justifyContent:
                  elements.length === 1 ? 'center' : 'space-between',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {elements}
            </Box>
          ) : null}
          {warningMenu}
        </Box>
      </Tooltip>
    );
  },
);
